// PENR_CENR_Officer Application Row
// Technical Staff Application Row

import ApplicationRow from '@/components/shared/ApplicationRow';
import ApplicationTableHeader from '@/components/shared/ApplicationTableHeader';
import CertificateViewModal from '@/components/sharedModals/CertificateViewModal';
import HistoryModal from '@/components/sharedModals/HistoryModal';
import ApplicationViewModal from '@/components/sharedModals/applicationViewModal/ApplicationViewModal';
import useActionHotkeys from '@/hooks/useActionHotkeys';
import useEscapeKey from '@/hooks/useEscapeKey';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ClipboardCheck, Eye, FileSignature, FileText, History, RotateCcw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import PCOAppReviewModal from './PCOAppReviewModal';
import PCOSignatureModal from './PCOSignatureModal';

const GET_APPLICATION_DETAILS = gql`
   query GetApplication($id: ID!) {
      getCSAWPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         registrationType
         ownerName
         address
         brand
         model
         serialNumber
         dateOfAcquisition
         powerOutput
         maxLengthGuidebar
         countryOfOrigin
         purchasePrice
         status
         currentStage
      }
      getCOVPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         name
         address
         cellphone
         purpose
         driverName
         driverLicenseNumber
         vehiclePlateNumber
         originAddress
         destinationAddress
         status
         currentStage
      }
   }
`;

const UPDATE_PERMIT_STAGE = gql`
   mutation UpdatePermitStage(
      $id: ID!,
      $currentStage: String!,
      $status: String!,
      $notes: String,
      $approvedByTechnicalStaff: Boolean,
      $awaitingPermitCreation: Boolean,
      $acceptedByTechnicalStaff: Boolean,
      $acceptedByPENRCENROfficer: Boolean,
      $approvedByPENRCENROfficer: Boolean
   ) {
      updatePermitStage(
         id: $id,
         currentStage: $currentStage,
         status: $status,
         notes: $notes,
         approvedByTechnicalStaff: $approvedByTechnicalStaff,
         awaitingPermitCreation: $awaitingPermitCreation,
         acceptedByTechnicalStaff: $acceptedByTechnicalStaff,
         acceptedByPENRCENROfficer: $acceptedByPENRCENROfficer,
         approvedByPENRCENROfficer: $approvedByPENRCENROfficer
      ) {
         id
         currentStage
         status
         acceptedByTechnicalStaff
         awaitingPermitCreation
         approvedByTechnicalStaff
         acceptedByPENRCENROfficer
         approvedByPENRCENROfficer
      }
   }
`;

const UNDO_ACCEPTANCE_CENRPENROFFICER = gql`
   mutation UndoAcceptanceCENRPENROfficer($id: ID!, $reviewedByChief: Boolean) {
      undoAcceptanceCENRPENROfficer(id: $id, reviewedByChief: $reviewedByChief) {
         id
         reviewedByChief
         history {
            notes
            timestamp
         }
      }
   }
`;

const GET_CERTIFICATE = gql`
   query GetCertificate($applicationId: ID!) {
      getCertificatesByApplicationId(applicationId: $applicationId) {
         id
         certificateNumber
         certificateStatus
         dateCreated
         dateIssued
         expiryDate
         dateApproved
         csawCertificateData {
            registrationType
            ownerName
            address
            purpose
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
         }
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber
         }
         ptprCertificateData {
            ownerName
            address
            contactNumber
            email
            octTctNumber
            lotNumber
            barangay
            municipality
            province
            totalLotArea
            areaDevotedToPlantation
            spacing
            treeSpeciesPlanted
            datePlanted
         }
         pltpCertificateData {
            name
            address
            lotNumber
            octNumber
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               treeStatus
               grossVolume
               netVolume
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         pltcpCertificateData {
            name
            occupation
            projectLocation
            projectAddress
            purpose
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               grossVolume
               netVolume
               treeCategory
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         applicantSignature {
            data
            contentType
         }
         orderOfPayment {
            officialReceipt {
               orNumber
               amount
               dateIssued
            }
         }
      }
   }
`;

const UNDO_SIGNATURE = gql`
   mutation UpdatePermitStage(
      $id: ID!,
      $currentStage: String!,
      $status: String!,
      $certificateSignedByPENRCENROfficer: Boolean!
   ) {
      updatePermitStage(
         id: $id,
         currentStage: $currentStage,
         status: $status,
         certificateSignedByPENRCENROfficer: $certificateSignedByPENRCENROfficer
      ) {
         id
         currentStage
         status
         certificateSignedByPENRCENROfficer
      }
   }
`;

const UPDATE_CERTIFICATE = gql`
   mutation UpdateCertificate($id: ID!, $certificateStatus: String, $signature: String) {
      updateCertificate(id: $id, certificateStatus: $certificateStatus, signature: $signature) {
         id
         certificateStatus
         signature {
            data
            contentType
         }
      }
   }
`;

const PCOApplicationRow = ({ app, onReviewComplete, currentTab, isMobile, isFirstRow }) => {
   const [isViewModalOpen, setIsViewModalOpen] = useState(false);
   const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
   const [isSignModalOpen, setIsSignModalOpen] = useState(false);
   const [isCertificateViewModalOpen, setIsCertificateViewModalOpen] = useState(false);
   const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
   const [undoAcceptanceCENRPENROfficer] = useMutation(UNDO_ACCEPTANCE_CENRPENROFFICER);
   const [updatePermitStage] = useMutation(UNDO_SIGNATURE);
   const [updateCertificate] = useMutation(UPDATE_CERTIFICATE);

   const { loading: certLoading, error: certError, data: certData } = useQuery(GET_CERTIFICATE, {
      variables: { applicationId: app.id },
      fetchPolicy: 'network-only',
      pollInterval: 10000
   });

   const handleReviewComplete = () => {
      setIsReviewModalOpen(false);
      onReviewComplete();
   };

   const handleUndo = async () => {
      try {
         const { data } = await undoAcceptanceCENRPENROfficer({
            variables: {
               id: app.id,
               currentStage: 'CENRPENRReview',
               status: 'In Progress',
               reviewedByChief: false
            }
         });

         // Only show success if we get data back
         if (data?.undoAcceptanceCENRPENROfficer) {
            toast.success('Application status undone successfully');
            onReviewComplete();
         } else {
            toast.error('Failed to undo application status, already reviewed by Chief RPS');
         }
      } catch (error) {
         console.error('Error undoing application status:', error);
         toast.error(error.message || 'Failed to undo application status');
      }
   };

   const handleUndoSignature = async () => {
      try {
         await updatePermitStage({
            variables: {
               id: app.id,
               currentStage: 'PendingSignatureByPENRCENROfficer',
               status: 'In Progress',
               certificateSignedByPENRCENROfficer: false
            }
         });

         // Also update the certificate status back to pending
         if (certData?.getCertificatesByApplicationId[0]?.id) {
            await updateCertificate({
               variables: {
                  id: certData.getCertificatesByApplicationId[0].id,
                  certificateStatus: 'Pending Signature',
                  signature: null
               }
            });
         }

         toast.success('Signature has been removed');
         onReviewComplete();
      } catch (error) {
         console.error('Error undoing signature:', error);
         toast.error('Failed to undo signature: ' + error.message);
      }
   };

   // Define actions for the application row
   const actions = [
      {
         icon: Eye,
         label: "View Application",
         onClick: () => setIsViewModalOpen(true),
         variant: "outline",
         className: "view-action",
         hotkey: "Ctrl + 1"
      },
      {
         icon: History,
         label: "View History",
         onClick: () => setIsHistoryModalOpen(true),
         variant: "outline",
         className: "history-action text-purple-600 hover:text-purple-800",
         hotkey: "Ctrl + 2"
      },
      currentTab === 'Applications for Review' && {
         icon: ClipboardCheck,
         label: "Review Application",
         onClick: () => setIsReviewModalOpen(true),
         variant: "outline",
         className: "text-blue-600 hover:text-blue-800",
         hotkey: "Ctrl + 3"
      },
      currentTab === 'Pending Signature' && {
         icon: FileSignature,
         label: "Sign Certificate",
         onClick: () => setIsSignModalOpen(true),
         variant: "outline",
         className: "sign-action text-blue-600 hover:text-blue-800",
         hotkey: "Ctrl + 4"
      },
      (currentTab === 'Pending Signature' ||
         currentTab === 'Signed Certificates' ||
         currentTab === 'Released Certificates' ||
         currentTab === 'Completed Applications' ||
         (app.hasCertificate && certData?.getCertificatesByApplicationId?.length > 0)) && {
         icon: FileText,
         label: "View Certificate",
         onClick: () => setIsCertificateViewModalOpen(true),
         variant: "outline",
         className: "view-certificate-action text-purple-600 hover:text-purple-800"
      },
      currentTab === 'Signed Certificates' && {
         icon: RotateCcw,
         label: "Undo Signature",
         onClick: handleUndoSignature,
         variant: "outline",
         className: "undo-action text-yellow-600 hover:text-yellow-800"
      },
      (currentTab === 'Approved Applications' || currentTab === 'Returned Applications') && {
         icon: RotateCcw,
         label: `Undo ${currentTab === 'Returned Applications' ? 'Return' : 'Approval'}`,
         onClick: handleUndo,
         variant: "outline",
         className: "text-yellow-600 hover:text-yellow-800"
      }
   ].filter(Boolean);

   useEffect(() => {
      if (certError) {
         console.error('Error fetching certificate:', certError);
         toast.error('Error loading certificate');
      }
   }, [certError]);

   useActionHotkeys({
      actions,
      activeMainTab: currentTab,
      disabled: isViewModalOpen || isReviewModalOpen || isSignModalOpen ||
         isCertificateViewModalOpen || isHistoryModalOpen
   });

   useEscapeKey(() => setIsViewModalOpen(false), isViewModalOpen);
   useEscapeKey(() => setIsReviewModalOpen(false), isReviewModalOpen);
   useEscapeKey(() => setIsSignModalOpen(false), isSignModalOpen);
   useEscapeKey(() => setIsCertificateViewModalOpen(false), isCertificateViewModalOpen);
   useEscapeKey(() => setIsHistoryModalOpen(false), isHistoryModalOpen);

   return (
      <>
         {isFirstRow && !isMobile && <ApplicationTableHeader />}
         <ApplicationRow
            application={app}
            actions={actions}
            isMobile={isMobile}
         />

         {/* Modals */}
         <ApplicationViewModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            application={app}
         />
         <PCOAppReviewModal
            isOpen={isReviewModalOpen}
            onClose={() => setIsReviewModalOpen(false)}
            application={app}
            onReviewComplete={handleReviewComplete}
         />
         <PCOSignatureModal
            isOpen={isSignModalOpen}
            onClose={() => setIsSignModalOpen(false)}
            application={{
               ...app,
               certificateId: certData?.getCertificatesByApplicationId[0]?.id
            }}
            onSignComplete={onReviewComplete}
         />
         <CertificateViewModal
            isOpen={isCertificateViewModalOpen}
            onClose={() => setIsCertificateViewModalOpen(false)}
            certificate={certData?.getCertificatesByApplicationId[0]}
            loading={certLoading}
            error={certError}
         />
         <HistoryModal
            isOpen={isHistoryModalOpen}
            onClose={() => setIsHistoryModalOpen(false)}
            history={app.history}
         />
      </>
   );
};

export default PCOApplicationRow;
